import { apiErrorMessage, customFetch } from '@agos/api-client'
import { usePutSettingsPizzaConfig } from '@agos/api-client/platform'
import { DEFAULT_PIZZA_CONFIG, normalisePizzaConfig, tt, type PizzaConfig } from '@agos/shared'
import { toast } from '@agos/ui'
import { useEffect, useState } from 'react'

/**
 * Loads and saves the pizza configuration blob.
 *
 * The backend does not describe this payload, so `@agos/shared`'s `PizzaConfig`
 * is the only definition of its shape — see `docs/pages/manager-menu/pizza-config.md`.
 *
 * Three things differ from legacy, all of them fixes:
 *
 *   FB-024  a failed save used to still show "Uložené!", because the caller set
 *           the flag regardless of the outcome. The confirmation now only
 *           appears when the request actually succeeded.
 *   FB-025  the caller can now ask whether there is unsaved work.
 *   FB-026  the local copy is written *after* the server accepts, not before.
 *           Legacy stores first, so a failed save leaves the browser holding a
 *           configuration the server has never seen — and serves it back on the
 *           next visit.
 */

const LOCAL_CACHE_KEY = 'fastkitchen_pizza_config'

function readLocalCache(): PizzaConfig {
  try {
    const raw = localStorage.getItem(LOCAL_CACHE_KEY)
    return raw ? normalisePizzaConfig(JSON.parse(raw) as Partial<PizzaConfig>) : DEFAULT_PIZZA_CONFIG
  } catch {
    return DEFAULT_PIZZA_CONFIG
  }
}

function writeLocalCache(config: PizzaConfig): void {
  try {
    localStorage.setItem(LOCAL_CACHE_KEY, JSON.stringify(config))
  } catch {
    // A kiosk with storage disabled still works for this session.
  }
}

export function usePizzaConfigSettings() {
  // The cached copy paints immediately; the server's answer replaces it.
  const [config, setConfig] = useState<PizzaConfig>(readLocalCache)
  const [savedSnapshot, setSavedSnapshot] = useState(() => JSON.stringify(readLocalCache()))
  const [justSaved, setJustSaved] = useState(false)

  const saveSettings = usePutSettingsPizzaConfig()

  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const response = await customFetch<Partial<PizzaConfig> | null>(
          '/api/v1/settings/pizza-config',
        )
        if (cancelled || !response || Array.isArray(response) || 'error' in response) return
        const fromServer = normalisePizzaConfig(response)
        setConfig(fromServer)
        setSavedSnapshot(JSON.stringify(fromServer))
        writeLocalCache(fromServer)
      } catch {
        // Keep whatever the cache painted.
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  const hasUnsavedChanges = JSON.stringify(config) !== savedSnapshot

  const save = () =>
    saveSettings.mutate(
      { data: config as unknown as Record<string, unknown> },
      {
        onSuccess: () => {
          writeLocalCache(config)
          setSavedSnapshot(JSON.stringify(config))
          setJustSaved(true)
          setTimeout(() => setJustSaved(false), 2000)
        },
        onError: (caught) =>
          toast({
            variant: 'destructive',
            title: tt('Uloženie zlyhalo'),
            description: apiErrorMessage(caught, tt('Nastavenia pizze sa nepodarilo uložiť.')),
          }),
      },
    )

  return {
    config,
    setConfig,
    save,
    isSaving: saveSettings.isPending,
    justSaved,
    hasUnsavedChanges,
  }
}
